import { Injectable, inject, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { StorageService } from './storage.service';
import { User, Profile, SessionKeys, StorageType, UserRoles } from '../models/user.model';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private authService = inject(AuthService);
  private storage = inject(StorageService);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);

  user = signal<User | null>(null);
  profile = signal<Profile | null>(null);
  isAuthenticated = signal<boolean>(false);

  constructor() {
    this.initializeUser();
  }

  private initializeUser(): void {
    const userStored = this.storage.getItem<User>(SessionKeys.USER, StorageType.SESSION);
    const profileStored = this.storage.getItem<Profile>(SessionKeys.PROFILE, StorageType.SESSION);

    if (userStored?.idUsuario && profileStored?.rol) {
      this.applyUser(userStored, profileStored);
    }

    // Escuchar cambios de Firebase Auth (con limpieza automática)
    this.authService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (firebaseUser) => {
        if (firebaseUser && !firebaseUser.isAnonymous) {
          await this.loadUserFromAuth(firebaseUser.uid, firebaseUser.email || '');
        } else {
          this.clearUser();
        }
      });
  }

  async loadUserFromAuth(uid: string, email: string): Promise<Profile> {
    const firebaseUser = this.authService.getCurrentUser();

    const roleFromClaims = firebaseUser ? await this.authService.getUserRole(firebaseUser) : null;

    let idEmpleado: number | undefined;
    try {
      const response = await firstValueFrom(this.authService.getMe());

      idEmpleado = response.data.id_empleado;
    } catch {
      // No bloquea el login si falla
    }

    const user: User = { idUsuario: uid, email, idEmpleado };
    const profile: Profile = { rol: roleFromClaims || UserRoles.EMPLOYEE_SELF };

    this.applyUser(user, profile);

    return profile;
  }

  private applyUser(user: User, profile: Profile): void {
    this.user.set(user);
    this.profile.set(profile);
    this.isAuthenticated.set(true);

    this.storage.setItem(SessionKeys.USER, user, StorageType.SESSION);
    this.storage.setItem(SessionKeys.PROFILE, profile, StorageType.SESSION);
  }

  private clearUser(): void {
    if (!this.isAuthenticated()) return;

    this.user.set(null);
    this.profile.set(null);
    this.isAuthenticated.set(false);

    this.storage.removeItem(SessionKeys.USER, StorageType.SESSION);
    this.storage.removeItem(SessionKeys.PROFILE, StorageType.SESSION);

    this.storage.removeItem('cv_update_employeeId');
    this.storage.removeItem('cv_update_requestId');

    this.authService.loginFirebase();
    this.router.navigate(['/session/login'], { replaceUrl: true });
  }

  hasRole(role: Profile['rol']): boolean {
    return this.profile()?.rol === role;
  }

  isHrAdmin(): boolean {
    return this.hasRole(UserRoles.HR_ADMIN);
  }

  isOpsAdmin(): boolean {
    return this.hasRole(UserRoles.OPS_ADMIN);
  }

  isEmployee(): boolean {
    return this.hasRole(UserRoles.EMPLOYEE_SELF);
  }

  getCurrentUser(): User | null {
    return this.user();
  }

  getCurrentProfile(): Profile | null {
    return this.profile();
  }

  async updateProfile(profile: Partial<Profile>): Promise<void> {
    const currentProfile = this.profile();
    if (!currentProfile) return;

    const updatedProfile = { ...currentProfile, ...profile };
    this.profile.set(updatedProfile);
    this.storage.setItem(SessionKeys.PROFILE, updatedProfile, StorageType.SESSION);
  }
}
